import { useCallback, useEffect, useState } from 'react';
import { Alert, App, Button, Drawer, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { RedoOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  AUTO_DISABLE_THRESHOLD,
  RETRY_LADDER,
  listDeliveries,
  replayDelivery,
  type DeliveryRow,
  type DeliveryState,
  type SubscriptionView,
} from '@/api/subscriptions';
import { normalizeApiError } from '@/api/errors';
import { InfoTip } from '@/help/InfoTip';
import { Term } from '@/help/Term';
import { formatAbsolute, formatRelative } from '@/lib/time';
import { explainDeliveriesReadRefusal, explainReplayRefusal } from './webhookErrors';

const { Text, Paragraph } = Typography;

const STATE_FILTERS: { value: '' | DeliveryState; label: string }[] = [
  { value: '', label: 'All states' },
  { value: 'PENDING', label: 'PENDING' },
  { value: 'RETRYING', label: 'RETRYING' },
  { value: 'DELIVERED', label: 'DELIVERED' },
  { value: 'DEAD', label: 'DEAD' },
  { value: 'SKIPPED', label: 'SKIPPED' },
];

/** Delivery state → tag; text always carries the state (colour never the sole signal). */
function StateTag({ state }: { state: string }) {
  const color =
    state === 'DELIVERED'
      ? 'green'
      : state === 'DEAD'
        ? 'red'
        : state === 'RETRYING'
          ? 'orange'
          : state === 'PENDING'
            ? 'blue'
            : 'default';
  return <Tag color={color}>{state}</Tag>;
}

/**
 * The per-subscription delivery ledger (S2): every attempt as the server recorded it, newest
 * first, with the ladder position and the REPLAY action on DEAD rows. Honesty rules: an empty
 * ledger is a true empty (deliveries start from subscription time — a new subscriber never
 * replays history); a 403 names the key and roles; a replay refusal (non-DEAD row raced to
 * another state) surfaces the server's 409 verbatim.
 */
export function DeliveryLedgerDrawer({
  subscription,
  onClose,
}: {
  subscription: SubscriptionView | null;
  onClose: () => void;
}) {
  const { message } = App.useApp();
  const [rows, setRows] = useState<DeliveryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [state, setState] = useState<'' | DeliveryState>('');
  const [replaying, setReplaying] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!subscription) return;
    setLoading(true);
    setRefusal(null);
    try {
      setRows(await listDeliveries(subscription.subscriptionId, state, 200));
    } catch (e) {
      setRows([]);
      setRefusal(explainDeliveriesReadRefusal(normalizeApiError(e)));
    } finally {
      setLoading(false);
    }
  }, [subscription, state]);

  useEffect(() => {
    if (subscription) {
      void load();
    } else {
      setRows([]);
      setState('');
      setRefusal(null);
    }
  }, [subscription, load]);

  const replay = useCallback(
    async (deliveryId: string) => {
      if (!subscription) return;
      setReplaying(deliveryId);
      try {
        const receipt = await replayDelivery(subscription.subscriptionId, deliveryId);
        message.success(
          `Replay queued: new delivery ${receipt.deliveryId} (${receipt.state}), replay of ${receipt.replayOf}.`,
        );
        await load();
      } catch (e) {
        message.error(explainReplayRefusal(normalizeApiError(e)));
      } finally {
        setReplaying(null);
      }
    },
    [subscription, message, load],
  );

  const columns: ColumnsType<DeliveryRow> = [
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          State <InfoTip surface="integrations" field="Delivery state" />
        </span>
      ),
      key: 'state',
      width: 120,
      render: (_, r) => (
        <div>
          <StateTag state={r.state} />
          {r.replayOf && (
            <div>
              <Text type="secondary" style={{ fontSize: 10.5 }} className="ol-mono">
                replay of {r.replayOf}
              </Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Event',
      key: 'event',
      render: (_, r) => (
        <div style={{ minWidth: 200 }}>
          <div className="ol-mono" style={{ fontSize: 12 }}>
            {r.eventType}
          </div>
          <Text type="secondary" style={{ fontSize: 10.5 }} className="ol-mono">
            event {r.eventId} · delivery {r.deliveryId}
          </Text>
        </div>
      ),
    },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          Attempts / next retry <InfoTip surface="integrations" field="Attempts / next retry" />
        </span>
      ),
      key: 'attempts',
      width: 170,
      render: (_, r) => (
        <div style={{ fontSize: 12 }}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{r.attempts}</span> attempt
          {r.attempts === 1 ? '' : 's'}
          <div>
            {r.state === 'DELIVERED' && r.deliveredAt ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                delivered {formatAbsolute(r.deliveredAt)}
              </Text>
            ) : r.nextAttemptAt && (r.state === 'RETRYING' || r.state === 'PENDING') ? (
              <Text type="secondary" style={{ fontSize: 11 }}>
                next {formatRelative(r.nextAttemptAt)}
              </Text>
            ) : (
              <Text type="secondary" style={{ fontSize: 11 }}>
                no retry scheduled
              </Text>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Last result',
      key: 'result',
      width: 150,
      render: (_, r) =>
        r.lastStatusCode == null && !r.lastError ? (
          <Text type="secondary" title="No attempt has completed yet — not a failure.">
            —
          </Text>
        ) : (
          <div style={{ fontSize: 12 }}>
            {r.lastStatusCode != null && <span>HTTP {r.lastStatusCode}</span>}
            {r.lastError && (
              <div>
                <Text type="danger" style={{ fontSize: 11 }} className="ol-mono">
                  {r.lastError}
                </Text>
              </div>
            )}
          </div>
        ),
    },
    {
      title: 'Recorded',
      key: 'createdAt',
      width: 150,
      render: (_, r) => (
        <Text type="secondary" style={{ fontSize: 11.5 }}>
          {r.createdAt ? formatAbsolute(r.createdAt) : '—'}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_, r) =>
        r.state === 'DEAD' ? (
          <Button
            size="small"
            icon={<RedoOutlined />}
            loading={replaying === r.deliveryId}
            onClick={() => void replay(r.deliveryId)}
            aria-label={`Replay delivery ${r.deliveryId}`}
          >
            Replay
          </Button>
        ) : null,
    },
  ];

  return (
    <Drawer
      open={subscription !== null}
      onClose={onClose}
      width={860}
      title={subscription ? `Deliveries — ${subscription.name}` : 'Deliveries'}
    >
      <Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: 0 }}>
        Every attempt, newest first, exactly as the ledger recorded it.{' '}
        <Term id="delivery-ladder">Retry ladder</Term>: {RETRY_LADDER.join(' → ')}, then{' '}
        <Term id="dead-letter">DEAD</Term>; {AUTO_DISABLE_THRESHOLD} consecutive failures
        auto-disable the subscription. A <Term id="webhook-replay">replay</Term> queues a DEAD
        delivery again as a new row.
      </Paragraph>
      <Space style={{ marginBottom: 12 }} wrap>
        <Select<'' | DeliveryState>
          value={state}
          onChange={(v) => setState(v)}
          options={STATE_FILTERS}
          style={{ width: 150 }}
          aria-label="Filter by delivery state"
        />
        <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
          Refresh
        </Button>
      </Space>

      {refusal ? (
        <Alert type="info" showIcon message="Ledger not readable" description={refusal} />
      ) : (
        <Table<DeliveryRow>
          rowKey="deliveryId"
          size="small"
          loading={loading}
          columns={columns}
          dataSource={rows}
          pagination={rows.length > 25 ? { pageSize: 25 } : false}
          locale={{
            emptyText:
              state === ''
                ? 'No deliveries recorded — not an error. Deliveries appear when a subscribed event occurs after subscription time (history is never replayed), or when you send a test ping.'
                : `No deliveries in state ${state}.`,
          }}
        />
      )}
    </Drawer>
  );
}
